import { SshMessageReader } from '../protocol/message-reader.js';

/**
 * Verify a host key signature from SSH_MSG_KEX_ECDH_REPLY.
 *
 * Supports:
 *   - ssh-ed25519
 *   - ssh-rsa with rsa-sha2-256 or rsa-sha2-512 signatures
 *
 * @param hostKeyBlob The full host key blob (including algorithm name prefix)
 * @param signatureBlob The full signature blob (including algorithm name prefix)
 * @param data The data that was signed (the exchange hash H)
 * @returns true if the signature is valid
 */
export async function verifyHostKey(
	hostKeyBlob: Uint8Array,
	signatureBlob: Uint8Array,
	data: Uint8Array,
): Promise<boolean> {
	// parse host key blob to determine algorithm
	const keyReader = new SshMessageReader(hostKeyBlob);
	const keyAlgorithm = keyReader.readStringAsText();

	// parse signature blob
	const sigReader = new SshMessageReader(signatureBlob);
	const sigAlgorithm = sigReader.readStringAsText();
	const signatureRaw = sigReader.readString();

	if (keyAlgorithm === 'ssh-ed25519') {
		if (sigAlgorithm !== 'ssh-ed25519') {
			throw new Error(`Unexpected signature algorithm "${sigAlgorithm}" for ssh-ed25519 host key`);
		}
		const publicKeyRaw = keyReader.readString();
		const publicKey = await crypto.subtle.importKey(
			'raw',
			new Uint8Array(publicKeyRaw),
			'Ed25519',
			false,
			['verify'],
		);
		return crypto.subtle.verify(
			'Ed25519',
			publicKey,
			new Uint8Array(signatureRaw),
			new Uint8Array(data),
		);
	}

	if (keyAlgorithm === 'ssh-rsa') {
		return verifyRsaHostKey(keyReader, sigAlgorithm, signatureRaw, data);
	}

	throw new Error(`Unsupported host key algorithm: ${keyAlgorithm}`);
}

/**
 * Verify an RSA host key signature.
 *
 * The host key blob (after "ssh-rsa" has been read):
 *   mpint  e   (public exponent)
 *   mpint  n   (modulus)
 *
 * The signature algorithm is "rsa-sha2-256" or "rsa-sha2-512".
 */
async function verifyRsaHostKey(
	keyReader: SshMessageReader,
	sigAlgorithm: string,
	signatureRaw: Uint8Array,
	data: Uint8Array,
): Promise<boolean> {
	let hash: string;
	if (sigAlgorithm === 'rsa-sha2-256') {
		hash = 'SHA-256';
	} else if (sigAlgorithm === 'rsa-sha2-512') {
		hash = 'SHA-512';
	} else if (sigAlgorithm === 'ssh-rsa') {
		hash = 'SHA-1';
	} else {
		throw new Error(`Unexpected signature algorithm "${sigAlgorithm}" for ssh-rsa host key`);
	}

	// read RSA public key components from the host key blob
	const eBytes = keyReader.readString();
	const nBytes = keyReader.readString();

	// build SPKI DER for Web Crypto import
	const spki = buildRsaSpki(nBytes, eBytes);

	const publicKey = await crypto.subtle.importKey(
		'spki',
		new Uint8Array(spki),
		{ name: 'RSASSA-PKCS1-v1_5', hash },
		false,
		['verify'],
	);

	return crypto.subtle.verify(
		'RSASSA-PKCS1-v1_5',
		publicKey,
		new Uint8Array(signatureRaw),
		new Uint8Array(data),
	);
}

/**
 * Build an RSA SubjectPublicKeyInfo (SPKI) DER structure from raw n and e bytes.
 *
 * SPKI structure:
 *   SEQUENCE {
 *     SEQUENCE {
 *       OID 1.2.840.113549.1.1.1 (rsaEncryption)
 *       NULL
 *     }
 *     BIT STRING {
 *       SEQUENCE {
 *         INTEGER n
 *         INTEGER e
 *       }
 *     }
 *   }
 *
 * The n and e bytes come from the SSH wire format (mpint-style: may have
 * a leading zero to indicate positive sign — that's already correct for DER INTEGER).
 */
function buildRsaSpki(nBytes: Uint8Array, eBytes: Uint8Array): Uint8Array {
	// DER-encode integers (the SSH mpint bytes are already in DER INTEGER content format)
	const nDer = derEncodeInteger(nBytes);
	const eDer = derEncodeInteger(eBytes);

	// inner SEQUENCE containing n, e
	const innerSeq = derEncodeSequence(concatBytes([nDer, eDer]));

	// BIT STRING wrapping the inner SEQUENCE (prepend a 0x00 "unused bits" byte)
	const bitStringContent = concatBytes([new Uint8Array([0x00]), innerSeq]);
	const bitString = derEncodeTagged(0x03, bitStringContent);

	// AlgorithmIdentifier SEQUENCE: OID(rsaEncryption) + NULL
	const rsaOid = new Uint8Array([0x06, 0x09, 0x2a, 0x86, 0x48, 0x86, 0xf7, 0x0d, 0x01, 0x01, 0x01]);
	const nullTag = new Uint8Array([0x05, 0x00]);
	const algId = derEncodeSequence(concatBytes([rsaOid, nullTag]));

	// outer SEQUENCE
	return derEncodeSequence(concatBytes([algId, bitString]));
}

// -- DER encoding helpers -----------------------------------------------

function derEncodeLength(length: number): Uint8Array {
	if (length < 0x80) {
		return new Uint8Array([length]);
	}
	// long form
	const bytes: number[] = [];
	let tmp = length;
	while (tmp > 0) {
		bytes.unshift(tmp & 0xff);
		tmp >>= 8;
	}
	return new Uint8Array([0x80 | bytes.length, ...bytes]);
}

function derEncodeTagged(tag: number, content: Uint8Array): Uint8Array {
	const len = derEncodeLength(content.length);
	return concatBytes([new Uint8Array([tag]), len, content]);
}

function derEncodeSequence(content: Uint8Array): Uint8Array {
	return derEncodeTagged(0x30, content);
}

function derEncodeInteger(valueBytes: Uint8Array): Uint8Array {
	return derEncodeTagged(0x02, valueBytes);
}

function concatBytes(arrays: Uint8Array[]): Uint8Array {
	let total = 0;
	for (const a of arrays) total += a.length;
	const result = new Uint8Array(total);
	let offset = 0;
	for (const a of arrays) {
		result.set(a, offset);
		offset += a.length;
	}
	return result;
}

/**
 * Extract the algorithm and first raw field from a host key blob.
 *
 * @param hostKeyBlob The full host key blob
 * @returns The algorithm name and first field (public key for Ed25519, e for RSA)
 */
export function parseHostKeyBlob(hostKeyBlob: Uint8Array): {
	algorithm: string;
	publicKey: Uint8Array;
} {
	const reader = new SshMessageReader(hostKeyBlob);
	const algorithm = reader.readStringAsText();
	const publicKey = reader.readString();
	return { algorithm, publicKey };
}
